import * as React from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Divider, InfoTooltip } from "@impero-com/ui";
import memoizeOne from "memoize-one";

import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import {
    RawEditableMultiSelectMultipleInput,
    RawReadOnlyMultiSelectMultipleInput,
} from "components/form/legacyFields/multiSelectMultipleInput";

import * as theme from "./theme.css";

interface RemindersBodyProps {
    readOnly: "EDIT" | "READ_ONLY";
    remindersBefore: number[][];
    remindersAfter: number[][];
    onChangeRemindersBefore: (v: number[]) => void;
    onChangeRemindersAfter: (v: number[]) => void;
}

class RemindersBody extends React.PureComponent<RemindersBodyProps> {
    private sortedRemindersBefore = memoizeOne(
        (remindersBefore: number[][]): number[][] => {
            remindersBefore.sort(Cmp.inverse(Cmp.primitive));
            return remindersBefore;
        }
    );

    private sortedRemindersAfter = memoizeOne(
        (remindersAfter: number[][]): number[][] => {
            remindersAfter.sort(Cmp.primitive);
            return remindersAfter;
        }
    );

    private handleChangeRemindersBefore = (changes: number[]) => {
        const sortedChanges = changes.slice();
        sortedChanges.sort(Cmp.inverse(Cmp.primitive));
        this.props.onChangeRemindersBefore(sortedChanges);
    };

    private handleChangeRemindersAfter = (changes: number[]) => {
        const sortedChanges = changes.slice();
        sortedChanges.sort(Cmp.primitive);
        this.props.onChangeRemindersAfter(sortedChanges);
    };

    render = () => {
        const { readOnly, remindersBefore, remindersAfter } = this.props;

        return (
            <>
                <Divider />
                <h1>
                    {t("CreateControl_Reminders_Title")}&nbsp;
                    <InfoTooltip
                        text={t("Control_ModifySchedule_RemindersInfo")}
                    />
                </h1>
                <MultiRemindersWidget
                    readOnly={readOnly}
                    name="remindersBefore"
                    label={t("Control_Reminders_BeforeDue")}
                    values={this.sortedRemindersBefore(remindersBefore)}
                    onChange={this.handleChangeRemindersBefore}
                    dueDateLabel={t("Control_Reminders_BeforeDueDate")}
                />
                <MultiRemindersWidget
                    readOnly={readOnly}
                    name="remindersAfter"
                    label={t("Control_Reminders_AfterDue")}
                    values={this.sortedRemindersAfter(remindersAfter)}
                    onChange={this.handleChangeRemindersAfter}
                    dueDateLabel={t("Control_Reminders_AfterDueDate")}
                />
            </>
        );
    };
}

const remindersOptions = [...Array(365).keys()].map((i) => i + 1);

interface RemindersWidgetProps {
    readOnly: "EDIT" | "READ_ONLY";
    name: string;
    label: string;
    values: number[][];
    onChange: (v: number[]) => void;
    dueDateLabel: string;
}

class MultiRemindersWidget extends React.PureComponent<RemindersWidgetProps> {
    render = (): React.ReactNode => {
        const { readOnly, name, label, values, onChange, dueDateLabel } =
            this.props;

        switch (readOnly) {
            case "EDIT": {
                return (
                    <BaseFormDisplay label={label}>
                        <div className={theme.remindersRow}>
                            <RawEditableMultiSelectMultipleInput<number>
                                name={name}
                                options={remindersOptions}
                                optionLabel={(value) =>
                                    `${value} ${t("Controls_Schedule_Day", {
                                        count: value,
                                    })}`
                                }
                                values={values}
                                className={theme.wideMultiSelect}
                                tagClassName={theme.remindersMultipleValuesTag}
                                onChange={onChange}
                            />
                            {dueDateLabel}
                        </div>
                    </BaseFormDisplay>
                );
            }
            case "READ_ONLY": {
                return (
                    <BaseFormDisplay label={label}>
                        <div className={theme.remindersRow}>
                            <RawReadOnlyMultiSelectMultipleInput<number>
                                name={name}
                                optionLabel={(value) =>
                                    `${value} ${t("Controls_Schedule_Day", {
                                        count: value,
                                    })}`
                                }
                                values={values}
                                tagClassName={theme.remindersMultipleValuesTag}
                            />
                            {dueDateLabel}
                        </div>
                    </BaseFormDisplay>
                );
            }
            default: {
                return assertNever(readOnly);
            }
        }
    };
}

export {
    type RemindersBodyProps,
    RemindersBody,
    type RemindersWidgetProps,
    MultiRemindersWidget,
};
